import { CompanyPicker } from '@/components/features/Companies/components/CompanyPicker';
import InputError from '@/components/layout/InputError';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Switch } from '@/components/ui/shadcn/switch';
import { useTranslations } from '@/hooks/useTranslations';
import axios from '@/lib/axios';
import { cn } from '@/lib/utils';
import { createPersonValidationSchema } from '@/lib/validations/person-validation';
import { useForm } from '@inertiajs/react';
import { BriefcaseBusiness, Building2, Info, Mail, Phone, User } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

const invalidFieldClassName = 'border-destructive ring-destructive/20 ring-[3px]';

interface LazyCompany {
    id: string;
    name: string;
    company_code: string;
    country: string;
}

interface PersonForm {
    first_name: string;
    last_name: string;
    email: string;
    position?: string;
    phone: string;
    accounting: boolean;
    company_id: string | null;
    [key: string]: string | number | string[] | boolean | null | undefined;
}

/** Shape returned by POST /api/people. */
export interface CreatedPerson {
    id: string;
    uuid: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string | null;
    position: string | null;
    accounting: boolean;
    company_id: string | null;
}

interface Props {
    onSuccess?: () => void;
    /**
     * 'page' keeps the standalone /people/create behaviour: a full Inertia POST
     * that redirects to the new contact. 'modal' saves over JSON instead, so the
     * host page (e.g. a half-filled lead form) is never navigated away from.
     */
    variant?: 'page' | 'modal';
    /** Preselects and locks the company. Modal variant only. */
    companyId?: string | null;
    companyName?: string | null;
    onCreated?: (person: CreatedPerson) => void;
    onCancel?: () => void;
}

export default function AddPersonForm({ onSuccess, variant = 'page', companyId = null, companyName = null, onCreated, onCancel }: Props) {
    const { t } = useTranslations();
    const isModal = variant === 'modal';
    const companyLocked = isModal && Boolean(companyId);
    const [selectedCompany, setSelectedCompany] = useState<LazyCompany | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const { data, setData, post, errors, setError, clearErrors, processing } = useForm<PersonForm>({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        position: '',
        accounting: false,
        company_id: companyId,
    });

    const validationSchema = useMemo(
        () =>
            createPersonValidationSchema({
                // A locked company is already decided by the host form.
                requireCompany: !companyLocked,
                messages: {
                    first_name_required: t('people.forms.add.validation.first_name_required', 'First name is required.'),
                    first_name_max: t('people.forms.add.validation.first_name_max', 'First name must be less than 255 characters.'),
                    last_name_required: t('people.forms.add.validation.last_name_required', 'Last name is required.'),
                    last_name_max: t('people.forms.add.validation.last_name_max', 'Last name must be less than 255 characters.'),
                    email_required: t('people.forms.add.validation.email_required', 'Email is required.'),
                    email_invalid: t('people.forms.add.validation.email_invalid', 'Please enter a valid email address.'),
                    email_max: t('people.forms.add.validation.email_max', 'Email must be less than 255 characters.'),
                    position_max: t('people.forms.add.validation.position_max', 'Position must be less than 255 characters.'),
                    company_required: t('people.forms.add.validation.company_required', 'Company is required.'),
                },
            }),
        [t, companyLocked],
    );

    /**
     * Saves over JSON so the host page keeps its state. The API enforces
     * `unique:people,email`, which zod cannot check, so server errors are mapped
     * back onto the fields.
     */
    const submitAsJson = async () => {
        setSubmitting(true);

        try {
            const response = await axios.post<CreatedPerson>('/api/people', data);
            onCreated?.(response.data);
            onSuccess?.();
        } catch (error) {
            const response = (error as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } }).response;
            const fieldErrors = response?.data?.errors;

            if (fieldErrors) {
                Object.entries(fieldErrors).forEach(([field, messages]) => {
                    setError(field as never, messages[0]);
                });
                toast.error(t('people.forms.add.validation.resolve_errors', 'Please fill in all required fields.'));
            } else {
                toast.error(response?.data?.message ?? t('people.forms.add.validation.save_failed', 'Could not save the contact. Please try again.'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        // React replays events through the component tree rather than the DOM
        // tree, so a portalled dialog does not escape the form that opened it:
        // without this, saving a contact from inside the lead form also submits
        // the lead and creates it.
        e.stopPropagation();
        clearErrors();

        const result = validationSchema.safeParse(data);
        if (!result.success) {
            const seen = new Set<string>();
            result.error.issues.forEach((issue) => {
                const field = issue.path?.[0] !== undefined ? String(issue.path[0]) : '';
                if (!field || seen.has(field)) return;
                seen.add(field);
                setError(field as never, issue.message);
            });
            toast.error(t('people.forms.add.validation.resolve_errors', 'Please fill in all required fields.'));
            return;
        }

        if (isModal) {
            void submitAsJson();

            return;
        }

        post(route('people.store'), {
            onSuccess: () => {
                onSuccess?.();
            },
            onError: () => {
                // Flash message will be shown by useFlashToasts hook if backend provides one
            },
        });
    };

    /*const handleCompanyCreated = (company: { id: string; name: string; company_code: string; country: string }) => {
        const newCompany: LazyCompany = {
            id: company.id,
            name: company.name,
            company_code: company.company_code,
            country: company.country
        };
        setSelectedCompany(newCompany);
        setData('company_id', company.id);
        toast.success('Company created successfully!');
    };*/

    return (
        <form onSubmit={submit} className="space-y-6 pb-6">
            <Card className="rounded-md">
                <CardHeader className="pb-4">
                    <div className="flex items-start gap-3">
                        <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                            1
                        </span>
                        <div className="space-y-1">
                            <CardTitle>{t('people.forms.add.sections.company_assignment', 'Company assignment')}</CardTitle>
                            <CardDescription>
                                {t('people.forms.add.sections.company_assignment_desc', 'Select the company this contact belongs to.')}
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-2">
                    {companyLocked ? (
                        <div className="bg-muted/40 flex items-center gap-3 rounded-sm border p-3">
                            <Building2 className="text-muted-foreground h-4 w-4 shrink-0" />
                            <div className="min-w-0">
                                <p className="truncate text-sm font-medium">{companyName}</p>
                                <p className="text-muted-foreground text-xs">
                                    {t('people.forms.add.dialog.company_locked', 'The contact will be added to :company.').replace(
                                        ':company',
                                        companyName ?? '',
                                    )}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <Label htmlFor="company_id">
                                {t('people.forms.add.labels.company', 'Company')} <span className="text-destructive">*</span>
                            </Label>
                            <CompanyPicker
                                selectedCompany={selectedCompany}
                                onCompanySelect={(company) => {
                                    setSelectedCompany(company);
                                    setData('company_id', company?.id || null);
                                    clearErrors('company_id');
                                }}
                                placeholder={t('people.forms.add.placeholders.select_company', 'Select a company...')}
                                className={cn(errors.company_id && invalidFieldClassName)}
                            />
                            <InputError className="mt-1" message={errors.company_id} />
                        </>
                    )}
                </CardContent>
            </Card>

            <Card className="rounded-md">
                <CardHeader className="pb-4">
                    <div className="flex items-start gap-3">
                        <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                            2
                        </span>
                        <div className="space-y-1">
                            <CardTitle>{t('people.forms.add.sections.personal_details', 'Personal details')}</CardTitle>
                            <CardDescription>
                                {t('people.forms.add.sections.personal_details_desc', 'Enter the main contact information.')}
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-5">
                    <div className="grid gap-5 md:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="first_name">
                                {t('people.forms.add.labels.first_name', 'First Name')} <span className="text-destructive">*</span>
                            </Label>
                            <div className="relative">
                                <User className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                <Input
                                    id="first_name"
                                    className={cn('pl-10', errors.first_name && invalidFieldClassName)}
                                    value={data.first_name}
                                    aria-invalid={!!errors.first_name}
                                    onChange={(e) => {
                                        setData('first_name', e.target.value);
                                        clearErrors('first_name');
                                    }}
                                    autoComplete="given-name"
                                    placeholder={t('people.forms.add.placeholders.first_name', 'First name')}
                                />
                            </div>
                            <InputError message={errors.first_name} />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="last_name">
                                {t('people.forms.add.labels.last_name', 'Last Name')} <span className="text-destructive">*</span>
                            </Label>
                            <div className="relative">
                                <User className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                <Input
                                    id="last_name"
                                    className={cn('pl-10', errors.last_name && invalidFieldClassName)}
                                    value={data.last_name}
                                    aria-invalid={!!errors.last_name}
                                    onChange={(e) => {
                                        setData('last_name', e.target.value);
                                        clearErrors('last_name');
                                    }}
                                    autoComplete="family-name"
                                    placeholder={t('people.forms.add.placeholders.last_name', 'Last name')}
                                />
                            </div>
                            <InputError message={errors.last_name} />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="email">
                                {t('people.forms.add.labels.email', 'Email')} <span className="text-destructive">*</span>
                            </Label>
                            <div className="relative">
                                <Mail className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                <Input
                                    id="email"
                                    type="email"
                                    className={cn('pl-10', errors.email && invalidFieldClassName)}
                                    value={data.email}
                                    aria-invalid={!!errors.email}
                                    onChange={(e) => {
                                        setData('email', e.target.value);
                                        clearErrors('email');
                                    }}
                                    autoComplete="email"
                                    placeholder={t('people.forms.add.placeholders.email_address', 'Email address')}
                                />
                            </div>
                            <InputError message={errors.email} />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="phone">{t('people.forms.add.labels.phone', 'Phone')}</Label>
                            <div className="relative">
                                <Phone className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                <Input
                                    id="phone"
                                    type="tel"
                                    className={cn('pl-10', errors.phone && invalidFieldClassName)}
                                    value={data.phone}
                                    aria-invalid={!!errors.phone}
                                    onChange={(e) => {
                                        setData('phone', e.target.value);
                                        clearErrors('phone');
                                    }}
                                    autoComplete="tel"
                                    placeholder={t('people.forms.add.placeholders.phone_number', 'Phone number')}
                                />
                            </div>
                            <InputError message={errors.phone} />
                        </div>

                        <div className="space-y-2 md:col-span-2">
                            <Label htmlFor="position">{t('people.forms.add.labels.position', 'Position')}</Label>
                            <div className="relative">
                                <BriefcaseBusiness className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                <Input
                                    id="position"
                                    className={cn('pl-10', errors.position && invalidFieldClassName)}
                                    value={data.position}
                                    aria-invalid={!!errors.position}
                                    onChange={(e) => {
                                        setData('position', e.target.value);
                                        clearErrors('position');
                                    }}
                                    autoComplete="organization-title"
                                    placeholder={t('people.forms.add.placeholders.position', 'Position')}
                                />
                            </div>
                            <InputError message={errors.position} />
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card className="rounded-md">
                <CardHeader className="pb-4">
                    <div className="flex items-start gap-3">
                        <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                            3
                        </span>
                        <div className="space-y-1">
                            <CardTitle>{t('people.forms.add.sections.accounting_contact', 'Accounting contact')}</CardTitle>
                            <CardDescription>
                                {t('people.forms.add.sections.accounting_contact_desc', 'Mark if this person can receive accounting documents.')}
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center space-x-3">
                        <Switch
                            id="accounting"
                            checked={data.accounting}
                            onCheckedChange={(checked) => {
                                setData('accounting', checked);
                                clearErrors('accounting');
                            }}
                        />
                        <span className="text-muted-foreground text-sm">
                            {t('people.forms.add.hints.accounting', 'This person can receive invoices and is from accounting')}
                        </span>
                    </div>
                    <InputError message={errors.accounting} />

                    <div className="border-primary/20 bg-primary/5 flex items-center gap-3 rounded-sm border p-4">
                        <Info className="text-primary h-5 w-5 shrink-0" />
                        <p className="text-muted-foreground text-sm">
                            {t(
                                'people.forms.add.hints.accounting_info',
                                'Enable this if the person is responsible for invoice receipt and accounting document access.',
                            )}
                        </p>
                    </div>
                </CardContent>
            </Card>

            <div
                className={cn(
                    'flex justify-between gap-2',
                    // In a dialog the sticky/blurred bar fights the dialog's own chrome.
                    !isModal && 'bg-background/95 sticky bottom-0 z-10 rounded-md border p-4 backdrop-blur',
                )}
            >
                <Button type="button" variant="outline" onClick={() => (isModal ? onCancel?.() : window.history.back())}>
                    {t('common.cancel', 'Cancel')}
                </Button>
                <Button type="submit" disabled={processing || submitting}>
                    {processing || submitting
                        ? t('people.forms.add.buttons.saving', 'Saving...')
                        : t('people.forms.add.buttons.save', 'Save Contact')}
                </Button>
            </div>
        </form>
    );
}
